import React, { lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import CommentIdentitySkeleton from "./components/skeletons/CommentIdentitySkeleton";
import DarNotaSkeleton from "./components/skeletons/DarNotaSkeleton";
import FavoritarSkeleton from "./components/skeletons/FavoritarSkeleton";
import LoginSkeleton from "./components/skeletons/LoginSkeleton";
import PerfilSkeleton from "./components/skeletons/PerfilSkeleton";
import RankingCarroSkeleton from "./components/skeletons/RankingCarroSkeleton";
import RankingLandingSkeleton from "./components/skeletons/RankingLandingSkeleton";
import RankingSkeleton from "./components/skeletons/RankingSkeleton";

type AnyComponent = React.ComponentType<any>;

type IslandConfig = {
  component: AnyComponent;
  skeleton?: React.ReactNode;
};

const registry: Record<string, IslandConfig> = {
  ranking: {
    component: lazy(
      () => import(/* webpackChunkName: "ranking" */ "./components/Ranking")
    ),
    skeleton: <RankingSkeleton />,
  },
  "ranking-carro": {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "ranking-carro" */ "./components/RankingCarro"
        )
    ),
    skeleton: <RankingCarroSkeleton />,
  },
  "ranking-landing": {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "ranking-landing" */ "./components/RankingLanding"
        )
    ),
    skeleton: <RankingLandingSkeleton />,
  },
  login: {
    component: lazy(
      () => import(/* webpackChunkName: "login" */ "./components/LoginIsland")
    ),
    skeleton: <LoginSkeleton />,
  },
  "comment-identity": {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "comment-identity" */ "./components/CommentIdentityIsland"
        )
    ),
    skeleton: <CommentIdentitySkeleton />,
  },
  "tag-manager": {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "tag-manager" */ "./components/TagManagerIsland"
        )
    ),
  },
  favoritar: {
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "favoritar" */ "./components/FavoritarIsland"
        )
    ),
    skeleton: <FavoritarSkeleton />,
  },
  "dar-nota": {
    component: lazy(
      () =>
        import(/* webpackChunkName: "dar-nota" */ "./components/DarNotaIsland")
    ),
    skeleton: <DarNotaSkeleton />,
  },
  perfil: {
    component: lazy(
      () => import(/* webpackChunkName: "perfil" */ "./components/PerfilIsland")
    ),
    skeleton: <PerfilSkeleton />,
  },
};

function kebabToCamel(key: string): string {
  return key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function readProps(el: HTMLElement): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const attr of Array.from(el.attributes)) {
    if (!attr.name.startsWith("data-")) continue;
    if (attr.name === "data-react-component") continue;
    props[kebabToCamel(attr.name.slice(5))] = attr.value;
  }
  return props;
}

function mountAll() {
  // Compat com o .NET: #ranking-root continua montando o componente "ranking"
  const legacy = document.getElementById("ranking-root");
  if (legacy && !legacy.hasAttribute("data-react-component")) {
    legacy.setAttribute("data-react-component", "ranking");
  }

  // Modo ilhas: qualquer <div data-react-component="..."> em qualquer página
  document
    .querySelectorAll<HTMLElement>("[data-react-component]")
    .forEach((el) => {
      // Defensivo: pula elementos que não são Element (Comment/Text node, removidos do DOM)
      if (!el || el.nodeType !== Node.ELEMENT_NODE || !document.contains(el)) {
        console.warn("[AutoPapo] elemento inválido pulado", el);
        return;
      }
      // Evita double-mount (caso o bundle seja carregado/executado mais de uma vez)
      if (el.dataset.reactMounted === "1") return;

      const name = el.dataset.reactComponent;
      if (!name) return;
      const config = registry[name];
      if (!config) {
        console.warn(`[AutoPapo] componente "${name}" não registrado`);
        return;
      }

      try {
        const Component = config.component;
        const props = readProps(el);
        createRoot(el).render(
          <React.StrictMode>
            <Suspense fallback={config.skeleton ?? null}>
              <Component {...props} />
            </Suspense>
          </React.StrictMode>
        );
        el.dataset.reactMounted = "1";
      } catch (err) {
        console.error(`[AutoPapo] falha ao montar "${name}"`, { el, err });
      }
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountAll);
} else {
  mountAll();
}
